import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Minus, Plus, UserPlus } from "lucide-react";
import {
  COUNTRY_LABELS,
  CUSTOMER_TYPE_LABELS,
  OPERATING_COUNTRIES,
  REGISTRATION_ROUTES,
  contentFor,
  currencyFor,
  formatPrice,
  registrationResponseSchema,
  viewerPackagePriceCents,
  viewerPriceCents,
  type Package,
  type Product,
  type RegistrationItem,
  type RegistrationResult,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Input, Select, Stepper } from "@/shared/ui/core";
import { SkeletonTable } from "@/shared/ui/ui";
import { AdminGuard, Combobox, DataTableFrame, type ComboboxOption } from "@/features/admin/components";
import { listAdminNetwork } from "@/features/admin/network/network.api";
import { fetchShopProducts } from "@/features/shop/shop.api";
import { fetchPackages } from "@/features/packages/packages.api";
import { localeOf } from "@/features/shop/shop.catalog";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Registros — el alta de un socio desde el admin (Anahí, 14-sep-2026).
 *
 * «Un wizard: tres pestañas —productos, membresías, paquetes— donde se
 * elige uno o más de cada; después nombre, correo, país y a quién se le
 * asigna. Con eso se cierra el registro, se agrega al sistema y se le
 * manda la invitación al correo para que ponga su contraseña.»
 *
 * Tres pasos y UNA petición al final (`POST /api/admin/registrations`):
 * la cuenta, la red, la orden (validada si el admin lo marca) y la
 * invitación salen juntas del servidor. Ver `contracts/registration.ts`.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminRegistrationPage() {
  return (
    <AdminGuard>
      <RegistrationWizard />
    </AdminGuard>
  );
}

type Tab = "product" | "membership" | "package";
type Selection = Record<string, number>; // `${kind}:${id}` → cantidad

const SOCIO = { isSignedIn: true, isFranchisee: false };

function RegistrationWizard() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [step, setStep] = useState(0);
  const [tab, setTab] = useState<Tab>("product");
  const [selection, setSelection] = useState<Selection>({});
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "MX" as "MX" | "US",
    sponsorCode: "",
    validateNow: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RegistrationResult | null>(null);

  const products = useQuery({ queryKey: ["admin", "registrations", "products"], queryFn: fetchShopProducts });
  const packages = useQuery({ queryKey: ["admin", "registrations", "packages"], queryFn: fetchPackages });
  const network = useQuery({ queryKey: ["admin", "network"], queryFn: listAdminNetwork });

  /*
   * ── El catálogo de patrocinadores (Anahí, 21-sep-2026) ────────────
   * Antes era un campo de texto donde se pegaba el enlace o el código;
   * ahora se ELIGE de la red: se busca por nombre, correo o código y el
   * valor guardado es el código de referido, que es lo que el servidor
   * resuelve. Solo entran los nodos activos: el servidor rechaza colgar
   * a alguien de una cuenta suspendida, así que no se ofrece.
   */
  const sponsorOptions = useMemo<ComboboxOption[]>(
    () =>
      (network.data ?? [])
        .filter((node) => node.status === "active")
        .map((node) => ({
          value: node.referralCode,
          label: node.displayName,
          hint: `${node.email} · ${node.referralCode} · ${CUSTOMER_TYPE_LABELS[node.customerType]}`,
        })),
    [network.data],
  );
  const sponsorName = sponsorOptions.find((o) => o.value === form.sponsorCode)?.label ?? null;

  const catalog = useMemo(() => {
    const all = products.data?.items ?? [];
    return {
      product: all.filter((p) => p.type !== "membership"),
      membership: all.filter((p) => p.type === "membership"),
      package: packages.data?.items ?? [],
    };
  }, [products.data, packages.data]);

  const currency = currencyFor(form.country);
  const keyOf = (kind: Tab, id: string) => `${kind}:${id}`;
  const qty = (kind: Tab, id: string) => selection[keyOf(kind, id)] ?? 0;
  const setQty = (kind: Tab, id: string, next: number) =>
    setSelection((current) => {
      const copy = { ...current };
      if (next <= 0) delete copy[keyOf(kind, id)];
      else copy[keyOf(kind, id)] = Math.min(99, next);
      return copy;
    });

  const items: RegistrationItem[] = Object.entries(selection).map(([key, quantity]) => {
    const [kind, id] = key.split(":") as [Tab, string];
    return { kind, id, quantity };
  });

  /** El precio de socio de cada cosa elegida, en la moneda del país. */
  function priceOf(kind: Tab, id: string): number | undefined {
    if (kind === "package") {
      const pkg = catalog.package.find((p) => p.id === id);
      return pkg ? viewerPackagePriceCents(pkg, currency, SOCIO) : undefined;
    }
    const product = [...catalog.product, ...catalog.membership].find((p) => p.id === id);
    return product ? viewerPriceCents(product, currency, SOCIO) : undefined;
  }
  function nameOf(kind: Tab, id: string): string {
    const found: Product | Package | undefined =
      kind === "package"
        ? catalog.package.find((p) => p.id === id)
        : [...catalog.product, ...catalog.membership].find((p) => p.id === id);
    return found ? contentFor(found.translations, locale).name : id;
  }
  const total = items.reduce((sum, item) => sum + (priceOf(item.kind, item.id) ?? 0) * item.quantity, 0);

  const registrar = useMutation({
    mutationFn: () =>
      authFetch(REGISTRATION_ROUTES.adminCreate, registrationResponseSchema, {
        baseUrl: "",
        method: "POST",
        body: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
          country: form.country,
          sponsorCode: form.sponsorCode.trim() ? form.sponsorCode.trim() : null,
          items,
          validateNow: form.validateNow,
        },
      }),
    onSuccess: (data) => setDone(data.result),
    onError: (cause) => setError(cause instanceof Error ? cause.message : t("No se pudo registrar")),
  });

  function next() {
    setError(null);
    if (step === 0 && items.length === 0) {
      setError(t("Elige al menos un producto, membresía o paquete"));
      return;
    }
    if (step === 1) {
      if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
        setError(t("Nombre, apellido y correo son obligatorios"));
        return;
      }
    }
    setStep((s) => Math.min(2, s + 1));
  }

  function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);
    registrar.mutate();
  }

  function reiniciar() {
    setDone(null);
    setSelection({});
    setForm({ firstName: "", lastName: "", email: "", phone: "", country: "MX", sponsorCode: "", validateNow: true });
    setStep(0);
    setTab("product");
  }

  if (done) {
    return (
      <>
        <PageHeader title={t("Registros")} description={t("Alta de un socio nuevo con su primer pedido y su invitación.")} />
        <DataTableFrame>
          <div className="grid gap-4 p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500 text-white">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-[17px] font-semibold text-ds-ink">
                  {t("{{name}} ya está en el sistema", { name: done.user.fullName })}
                </h2>
                <p className="text-[13px] text-ds-ink-3">{done.user.email}</p>
              </div>
            </div>
            <ul className="grid gap-1.5 text-[14px] text-ds-ink-2">
              <li>
                {done.sponsorName
                  ? t("En la red bajo {{sponsor}}.", { sponsor: done.sponsorName })
                  : t("En la red como raíz (sin patrocinador).")}
              </li>
              <li>
                {t("Pedido {{folio}} por {{total}}", {
                  folio: done.order.folio,
                  total: formatPrice(done.order.totalCents, done.order.currency),
                })}
                {" — "}
                {done.validated
                  ? t("validado: puntos, membresía anual y comisiones ya corrieron.")
                  : t("pendiente de validar en Órdenes.")}
              </li>
              <li>
                {done.invitationSent
                  ? t("Invitación enviada a su correo con el enlace para crear su contraseña (vale 7 días).")
                  : t("La invitación NO salió (revisa el correo del servidor); puede entrar con «¿Olvidaste tu contraseña?».")}
              </li>
            </ul>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button leftIcon={<UserPlus />} onClick={reiniciar}>
                {t("Registrar otro socio")}
              </Button>
              <Button as={Link} to={`/admin/orders/${done.order.id}`} variant="secondary">
                {t("Ver el pedido")}
              </Button>
            </div>
          </div>
        </DataTableFrame>
      </>
    );
  }

  const loading = products.isPending || packages.isPending;
  const TABS: { key: Tab; label: string }[] = [
    { key: "product", label: t("Productos") },
    { key: "membership", label: t("Membresías") },
    { key: "package", label: t("Paquetes") },
  ];

  return (
    <>
      <PageHeader title={t("Registros")} description={t("Alta de un socio nuevo con su primer pedido y su invitación.")} />

      <DataTableFrame>
        <form onSubmit={enviar} className="grid gap-5 p-5 sm:p-6">
          <Stepper steps={[t("Qué compra"), t("Quién es"), t("Confirmar")]} current={step} />

          {/* ── Paso 1 · productos, membresías, paquetes ──────────────── */}
          {step === 0 && (
            <div className="grid gap-4">
              <div className="flex flex-wrap gap-2" role="tablist">
                {TABS.map((item) => {
                  const count = items.filter((i) => i.kind === item.key).reduce((s, i) => s + i.quantity, 0);
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      aria-selected={tab === item.key}
                      onClick={() => setTab(item.key)}
                      className={
                        tab === item.key
                          ? "rounded-ds-pill bg-ds-violet-pop px-4 py-1.5 text-[13px] font-semibold text-white"
                          : "rounded-ds-pill border border-ds-border bg-white px-4 py-1.5 text-[13px] font-semibold text-ds-ink-3 hover:border-ds-violet-pop"
                      }
                    >
                      {item.label}
                      {count > 0 ? ` · ${count}` : ""}
                    </button>
                  );
                })}
              </div>

              {loading ? (
                <SkeletonTable rows={4} columns={3} />
              ) : (
                <ul className="grid gap-2">
                  {catalog[tab].length === 0 && (
                    <li className="text-[13px] text-ds-ink-3">{t("No hay nada publicado en esta pestaña.")}</li>
                  )}
                  {catalog[tab].map((entry) => {
                    const id = entry.id;
                    const price = priceOf(tab, id);
                    const n = qty(tab, id);
                    return (
                      <li
                        key={id}
                        className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${
                          n > 0 ? "border-ds-violet-pop bg-ds-lavender-bg/40" : "border-ds-border bg-white"
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-[14px] font-semibold text-ds-ink">{contentFor(entry.translations, locale).name}</p>
                          <p className="text-[12px] text-ds-ink-3">
                            {price !== undefined ? `${formatPrice(price, currency)} ${currency}` : t("Sin precio en {{currency}}", { currency })}
                            {" · "}
                            {t("precio de socio")}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="secondary" size="sm" aria-label={t("Quitar uno")} onClick={() => setQty(tab, id, n - 1)} disabled={n === 0}>
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-mono text-[14px] font-semibold">{n}</span>
                          <Button type="button" variant="secondary" size="sm" aria-label={t("Agregar uno")} onClick={() => setQty(tab, id, n + 1)} disabled={price === undefined}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <p className="text-[13px] text-ds-ink-3">
                {t("Elegido")}: <span className="font-semibold text-ds-ink">{items.reduce((s, i) => s + i.quantity, 0)}</span> ·{" "}
                {t("Total")}: <span className="font-semibold text-ds-ink">{formatPrice(total, currency)} {currency}</span>
              </p>
            </div>
          )}

          {/* ── Paso 2 · quién es y bajo quién ────────────────────────── */}
          {step === 1 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={t("Nombre")} value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
              <Input label={t("Apellido")} value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
              <Input label={t("Correo electrónico")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required hint={t("Aquí llega la invitación para crear su contraseña.")} />
              <Input label={t("Teléfono (opcional)")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+52 55 1234 5678" hint={t("Con lada, formato +52…")} />
              <label className="grid gap-1 text-[12.5px] font-medium text-ds-ink-3">
                {t("País")}
                <Select
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value as "MX" | "US" })}
                  options={OPERATING_COUNTRIES.map((c) => ({ value: c, label: COUNTRY_LABELS[c] }))}
                />
                <span className="text-[12px] font-normal">{t("Decide la moneda del pedido: MX en pesos, US en dólares.")}</span>
              </label>
              <Combobox
                label={t("A quién se le asigna (patrocinador)")}
                options={sponsorOptions}
                value={form.sponsorCode}
                onChange={(code) => setForm({ ...form, sponsorCode: code })}
                placeholder={t("Busca por nombre, correo o código…")}
                disabled={network.isPending}
                emptyLabel={t("Nadie en la red con ese nombre, correo o código.")}
                hint={
                  network.isError
                    ? t("No se pudo cargar la red; recarga la página.")
                    : t("Sin elegir a nadie, cuelga de la raíz.")
                }
              />
            </div>
          )}

          {/* ── Paso 3 · confirmar ────────────────────────────────────── */}
          {step === 2 && (
            <div className="grid gap-4">
              <div className="rounded-2xl border border-ds-border bg-white p-4">
                <p className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">{t("Socio")}</p>
                <p className="text-[15px] font-semibold text-ds-ink">{form.firstName} {form.lastName}</p>
                <p className="text-[13px] text-ds-ink-3">
                  {form.email} · {COUNTRY_LABELS[form.country]}
                  {sponsorName ? ` · ${t("bajo")} ${sponsorName} (${form.sponsorCode})` : ` · ${t("raíz")}`}
                </p>
              </div>
              <div className="rounded-2xl border border-ds-border bg-white p-4">
                <p className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">{t("Pedido")}</p>
                <ul className="mt-1 grid gap-1 text-[13.5px]">
                  {items.map((item) => (
                    <li key={`${item.kind}:${item.id}`} className="flex justify-between gap-3">
                      <span>{item.quantity} × {nameOf(item.kind, item.id)}</span>
                      <span className="font-mono">{formatPrice((priceOf(item.kind, item.id) ?? 0) * item.quantity, currency)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 flex justify-between border-t border-ds-border pt-2 text-[14px] font-semibold">
                  <span>{t("Total")}</span>
                  <span className="font-mono">{formatPrice(total, currency)} {currency}</span>
                </p>
              </div>
              <label className="flex items-start gap-3 text-[13.5px] text-ds-ink-2">
                <input
                  type="checkbox"
                  checked={form.validateNow}
                  onChange={(e) => setForm({ ...form, validateNow: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-ds-violet-pop"
                />
                <span>
                  <span className="font-semibold text-ds-ink">{t("Validar el pedido ahora (ya cobrado)")}</span>
                  <br />
                  {t("Al validarlo corren sus puntos, su membresía anual, su reloj y las comisiones de su línea. Si lo dejas sin marcar, queda pendiente en Órdenes.")}
                </span>
              </label>
              <Alert variant="info">
                {t("Al terminar se le manda al correo la invitación con el enlace para crear su contraseña (vale 7 días) y las indicaciones para entrar.")}
              </Alert>
            </div>
          )}

          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="flex flex-wrap justify-between gap-2 border-t border-ds-border pt-4">
            <div>
              {step > 0 && (
                <Button type="button" variant="ghost" onClick={() => setStep((s) => s - 1)}>
                  {t("Atrás")}
                </Button>
              )}
            </div>
            {step < 2 ? (
              <Button type="button" onClick={next}>
                {t("Siguiente")}
              </Button>
            ) : (
              <Button type="submit" leftIcon={<UserPlus />} loading={registrar.isPending}>
                {t("Registrar socio")}
              </Button>
            )}
          </div>
        </form>
      </DataTableFrame>
    </>
  );
}
